import React, { useEffect, useState } from 'react'
import { Box, Button, Card, Grid, makeStyles } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { Formik, Form } from 'formik'
import ViewHeader from 'components/ViewHeader'
import ProgressButton from 'components/ProgressButton'
import { fetchSpeakerById, resetSpeaker } from 'store/speaker'
import { addSpeaker, updateSpeakerById } from 'store/speaker'
import ActionBar from 'components/ActionBar'
import CustomTextField from 'components/Form/CustomTextField'
import { Collection, SPEAKER_INFO_TEXT } from 'constants/index'
import { speakerFormSchema } from 'schemas/speaker'
import { toast } from 'react-toastify'
import PollingListing from 'views/admin/subjects/PollingForm/PollingListing'
import pollingSlice from 'store/polling'
import { submitSubjectData } from 'store/subject'
import { omit } from 'lodash'
import AvatarPicker from 'components/AvatarPicker'
import { uploadImageFirstTime } from 'utils'
import ReactQuill from 'react-quill'
import { modules } from 'views/admin/settings/SettingForm/TemplateForm'
import ErrorMessage from 'components/ErrorMessage'
import InfoIconAlongText from 'components/InfoIconAlongText'

const SpeakerForm = ({ displayContext, onSuccess, onCloseModal }) => {
  const classes = useStyles()
  const { speakerId } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const [profileImage, setProfileImage] = useState(null)
  const [resetImage, setResetImage] = useState(false)
  const [loading, setLoading] = useState(false)

  const { user } = useSelector((state) => state.auth)
  const { speaker } = useSelector((state) => state.speaker)
  const { conference } = useSelector((state) => state.conference)

  const initialValues = {
    fullName: speaker?.fullName ?? '',
    email: speaker?.email ?? '',
    company: speaker?.company ?? '',
    position: speaker?.position ?? '',
    bio: speaker?.bio ?? '',
    websiteUrl: speaker?.websiteUrl ?? ''
  }

  const handleImageChange = (file) => {
    setProfileImage(file)
  }

  const goBack = () => {
    dispatch(resetSpeaker())
    dispatch(pollingSlice.actions.setPolls([]))
    history.goBack()
  }

  useEffect(() => {
    if (speakerId) {
      dispatch(fetchSpeakerById(speakerId))
    }
    return () => dispatch(resetSpeaker())
  }, [dispatch, speakerId])

  const handleUpload = async (name) => {
    const modifiedSpeaker = omit(speaker, 'id')
    await dispatch(
      updateSpeakerById(speakerId, { ...modifiedSpeaker, imageName: name }, user?.conferenceId)
    )
  }

  const handleDelete = async () => {
    const modifiedSpeaker = omit(speaker, 'id')
    await dispatch(
      updateSpeakerById(speakerId, { ...modifiedSpeaker, imageName: null }, user?.conferenceId)
    )
  }

  const onCancel = () => {
    if (displayContext === 'page') {
      goBack()
    } else {
      onCloseModal()
    }
  }

  const onSubmit = async ({ fullName, email, bio, company, position, websiteUrl }) => {
    setLoading(true)
    const payload = {
      fullName,
      email,
      bio,
      company,
      position,
      websiteUrl
    }
    if (profileImage) {
      payload.imageName = await uploadImageFirstTime(
        profileImage,
        Collection.SPEAKERS,
        user?.conferenceId
      )
    }

    let res, message
    if (speakerId) {
      res = await dispatch(updateSpeakerById(speakerId, payload, user?.conferenceId))
      speaker?.subjectId && (await dispatch(submitSubjectData(speaker?.subjectId, payload)))
      message = 'Speaker updated successfully.'
    } else {
      res = await dispatch(addSpeaker(payload, user?.conferenceId))
      message = 'Speaker added successfully.'
    }
    setLoading(false)
    setProfileImage(null)
    setResetImage(true)
    if (res && displayContext === 'page') {
      toast.success(message)
    }
    displayContext === 'modal' && onSuccess && onSuccess(res)
  }

  return (
    <Card
      elevation={displayContext === 'page' ? 3 : 0}
      className={displayContext === 'page' ? classes.card : classes.styledCard}>
      {displayContext === 'page' && (
        <ViewHeader title={speakerId ? 'Edit speaker' : 'Create speaker'}>
          <ActionBar actionText="Back" onAction={goBack} />
        </ViewHeader>
      )}
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={speakerFormSchema}
        validateOnBlur={true}
        validateOnChange={true}
        validateOnMount={true}>
        {({
          isValid,
          values,
          touched,
          errors,
          setFieldValue,
          submitForm,
          resetForm,
          validateForm
        }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Email" name="email" type="email" disabled={!!speakerId} />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Full Name" name="fullName" type="text" />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Institution" name="company" type="text" />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Position" name="position" type="text" />
              </Grid>
              <Grid item xs={12} lg={8}>
                <CustomTextField label="Website URL" name="websiteUrl" type="text" />
              </Grid>
              <Grid item xs={12} lg={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label className={classes.label}>
                    Bio
                    <InfoIconAlongText infoText={SPEAKER_INFO_TEXT.bio} />
                  </label>
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    value={values.bio}
                    onChange={(value) => setFieldValue('bio', value)}
                    placeholder="Write bio here..."
                  />
                  <ErrorMessage message={touched?.bio && errors?.bio} />
                </Box>
              </Grid>

              {conference?.havePolls && speakerId && (
                <Grid item xs={12} md={8}>
                  <PollingListing speakerId={speakerId} forApproval={false} subject={null} />
                </Grid>
              )}

              <Grid item xs={12} lg={6}>
                <AvatarPicker
                  title="Profile Picture"
                  subTitle="Allowed Format: png/jpg ; Dimensions : 320x320"
                  path={Collection.SPEAKERS}
                  handleCustomChange={handleImageChange}
                  onUpload={handleUpload}
                  onDelete={handleDelete}
                  imageName={speakerId ? speaker?.imageName : null}
                  resetImage={resetImage}
                  conferenceId={user?.conferenceId}
                  infoText={SPEAKER_INFO_TEXT.profile}
                  firsTimeUpload={!speakerId}
                  imageProps={{ style: { objectFit: 'cover', objectPosition: '50% 12%' } }}
                />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" spacing={2}>
              <Grid item>
                <Button size="large" disabled={loading} onClick={onCancel}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <ProgressButton
                  type="button"
                  label="Save"
                  inProgress={loading}
                  disabled={loading}
                  onClick={async () => {
                    validateForm()
                    await submitForm()
                    if (isValid && displayContext === 'page') goBack()
                  }}
                />
              </Grid>
              {displayContext === 'page' && (
                <Grid item>
                  <ProgressButton
                    type="button"
                    label="Save and Create New"
                    inProgress={loading}
                    disabled={loading}
                    onClick={async () => {
                      validateForm()
                      await submitForm()
                      if (isValid) {
                        resetForm()
                        history.replace(`/admin/speakers/new`)
                      }
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    </Card>
  )
}
export default SpeakerForm

const useStyles = makeStyles({
  label: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    color: '#606060',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginLeft: '0.2rem'
  },
  card: {
    padding: '24px',
    overflow: 'visible'
  },
  styledCard: {
    paddingTop: '1rem'
  }
})
